<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Plus, FolderKanban } from 'lucide-svelte';
  import { projectsApi } from '$lib/api/projects';
  import { adminUsersApi } from '$lib/api/admin';
  import { deploysApi } from '$lib/api/deploys';
  import { getErrorMessage } from '$lib/api/client';
  import { userHasProject } from '$lib/utils/projects';
  import { teamKind, teamLabel } from '$lib/utils/format';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import ProjectCard from './ProjectCard.svelte';
  import ProjectModal from './ProjectModal.svelte';
  import type { Project, Team } from '$lib/types';
  import { TEAMS } from '$lib/types';

  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list()
  }));
  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users', { search: '' }],
    queryFn: () => adminUsersApi.list({ per_page: 100 })
  }));
  const deploysQuery = createQuery(() => ({
    queryKey: ['deploys'],
    queryFn: () => deploysApi.list()
  }));

  const developerCount = $derived(usersQuery.data?.results.filter((u) => u.role === 'member').length ?? 0);

  let teamFilter = $state<Team | 'all'>('all');
  const visibleProjects = $derived(
    (projectsQuery.data?.results ?? []).filter((p) => teamFilter === 'all' || p.team === teamFilter)
  );

  function devsFor(projectId: number) {
    return (usersQuery.data?.results ?? []).filter((u) => u.role === 'member' && userHasProject(u, projectId));
  }
  function lastDeployFor(projectId: number) {
    const list = (deploysQuery.data?.results ?? []).filter((d) => d.project_id === projectId);
    if (!list.length) return null;
    return [...list].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  }

  let modalProject = $state<Project | null | undefined>(undefined);

  function openCreate() {
    modalProject = null;
  }
  function openEdit(p: Project) {
    modalProject = p;
  }
  function closeModal() {
    modalProject = undefined;
  }
</script>

<div class="page-head">
  <div>
    <h1 class="h-title">Projects</h1>
    <div class="h-sub">{projectsQuery.data?.count ?? 0} projects · {developerCount} developers</div>
  </div>
  <span class="spacer"></span>
  <button type="button" class="btn btn-primary" onclick={openCreate}>
    <Plus size={16} /> New project
  </button>
</div>

{#if projectsQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading projects…</p>
{:else if projectsQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(projectsQuery.error)}</p>
{:else if projectsQuery.data?.results.length}
  <div style="display:flex;gap:8px;margin-bottom:16px">
    <button type="button" class="chip chip-{teamFilter === 'all' ? 'teal' : 'gray'}" style="cursor:pointer;border:none;font:inherit" onclick={() => (teamFilter = 'all')}>
      All teams
    </button>
    {#each TEAMS as t (t)}
      <button
        type="button"
        class="chip chip-{teamFilter === t ? teamKind(t) : 'gray'}"
        style="cursor:pointer;border:none;font:inherit"
        onclick={() => (teamFilter = t)}
      >
        {teamLabel(t)}
      </button>
    {/each}
  </div>
  {#if visibleProjects.length}
    <div class="grid-cards">
      {#each visibleProjects as p (p.id)}
        <ProjectCard project={p} devs={devsFor(p.id)} lastDeploy={lastDeployFor(p.id)} onEdit={openEdit} />
      {/each}
    </div>
  {:else}
    <p style="color:var(--t3);font-size:13.5px">No projects in this team.</p>
  {/if}
{:else}
  <EmptyState icon={FolderKanban} heading="No projects yet" sub="Create your first project to start tracking board work, tickets and deploys.">
    {#snippet action()}
      <button type="button" class="btn btn-primary" onclick={openCreate}><Plus size={16} /> New project</button>
    {/snippet}
  </EmptyState>
{/if}

{#if modalProject !== undefined}
  <ProjectModal project={modalProject} members={(usersQuery.data?.results ?? []).filter((u) => u.role === 'member')} onClose={closeModal} />
{/if}
